import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import * as LucideIcons from 'lucide-react'
import { ChevronDown, HelpCircle, Lightbulb, Settings } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { BetaBadge } from '@/components/ui/BetaBadge.tsx'
import { useModulesStore } from '@/stores/modules.ts'
import { useSapPermissionsStore } from '@/stores/sapPermissions.ts'
import { getMenuCategories } from '@shared/types'
import type { SapPermission } from '@shared/types'

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
  description?: string
  /** Module ID for beta badge lookup and access filtering */
  moduleId?: string
  /** SAP permission key — if set, item is disabled when user lacks this SAP permission */
  sapPermission?: SapPermission
  /** If true, NavLink uses exact path match (needed for "/" to avoid matching every route) */
  end?: boolean
  /** Sub-group label for multi-column dropdown rendering */
  subGroup?: string
}

interface NavCategory {
  id: string
  label: string
  items: NavItem[]
}

/** Resolve a Lucide icon name (string) to its component, falling back to a placeholder. */
function resolveLucideIcon(name: string): React.ElementType {
  const icon = (LucideIcons as unknown as Record<string, React.ElementType>)[name]
  return icon ?? HelpCircle
}

/** Stable category id for routing/state (lowercased, no spaces/diacritics). */
function categoryId(label: string): string {
  return label
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Main navigation, derived from `MODULE_DEFINITIONS` (single source of truth
 * shared with the settings module list).
 */
const navCategories: NavCategory[] = getMenuCategories().map((group) => ({
  id: categoryId(group.category),
  label: group.category,
  items: group.entries.map((entry) => ({
    to: entry.route,
    icon: resolveLucideIcon(entry.icon),
    label: entry.label,
    description: entry.description,
    moduleId: entry.moduleId,
    sapPermission: entry.sapPermission,
    end: entry.end,
    subGroup: entry.subGroup,
  })),
}))

const utilityItems: NavItem[] = [
  { to: '/features', icon: Lightbulb, label: 'Feature-Wünsche' },
  { to: '/einstellungen', icon: Settings, label: 'Einstellungen' },
]

export { navCategories, utilityItems }
export type { NavItem, NavCategory }

function MegaMenuItem({ item }: { item: NavItem }) {
  const Icon = item.icon
  const isBeta = useModulesStore((s) => item.moduleId ? s.isBeta(item.moduleId) : false)
  const sapAllowed = useSapPermissionsStore((s) =>
    item.sapPermission ? s.isAllowed(item.sapPermission) : true,
  )
  const sapConfigured = useSapPermissionsStore((s) =>
    item.sapPermission ? (s.permissions?.configured ?? true) : true,
  )

  // SAP item but no credentials → show as disabled with hint
  const disabled = item.sapPermission && (!sapConfigured || !sapAllowed)

  if (disabled) {
    return (
      <div
        className="flex items-start gap-3 px-3 py-2.5 rounded text-gray-400 cursor-not-allowed"
        title={!sapConfigured ? 'SAP-Zugangsdaten nicht hinterlegt (Einstellungen)' : 'Keine SAP-Berechtigung'}
      >
        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium flex items-center gap-1.5">
            {item.label}
            {isBeta && <BetaBadge />}
          </p>
          <p className="text-xs mt-0.5">
            {!sapConfigured ? 'Zugangsdaten fehlen' : 'Keine Berechtigung'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'flex items-start gap-3 px-3 py-2.5 rounded transition-colors group',
          isActive
            ? 'bg-brand-50 text-brand-700'
            : 'text-gray-700 hover:bg-gray-50',
        )
      }
    >
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-brand-500 group-hover:text-brand-600" />
      <div className="flex-1">
        <p className="text-sm font-medium flex flex-wrap items-center gap-1.5">
          {item.label}
          {isBeta && <BetaBadge />}
        </p>
        {item.description && (
          <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
        )}
      </div>
    </NavLink>
  )
}

/** Groups items by their subGroup label. Returns null if no item has a subGroup. */
function groupBySubGroup(items: NavItem[]): Map<string, NavItem[]> | null {
  const hasSubGroups = items.some((i) => i.subGroup)
  if (!hasSubGroups) return null
  const map = new Map<string, NavItem[]>()
  for (const item of items) {
    const key = item.subGroup ?? ''
    const list = map.get(key) ?? []
    list.push(item)
    map.set(key, list)
  }
  return map
}

function DropdownPanel({
  category,
  onMouseEnter,
  onMouseLeave,
}: {
  category: NavCategory
  onMouseEnter: () => void
  onMouseLeave: () => void
}) {
  const subGroups = groupBySubGroup(category.items)
  const panelRef = useRef<HTMLDivElement>(null)
  const [alignRight, setAlignRight] = useState(false)
  const [splitColumns, setSplitColumns] = useState(false)

  useLayoutEffect(() => {
    if (!panelRef.current) return
    const rect = panelRef.current.getBoundingClientRect()
    if (rect.right > window.innerWidth) {
      setAlignRight(true)
    }
    if (rect.bottom > window.innerHeight * 0.9) {
      setSplitColumns(true)
    }
  }, [])

  if (subGroups) {
    const columns = [...subGroups.entries()].filter(([key]) => key !== '')
    return (
      <div
        ref={panelRef}
        className={cn(
          'absolute top-full mt-1 bg-white rounded shadow-xl border border-gray-200 z-50',
          alignRight ? 'right-0' : 'left-0',
        )}
        style={{ minWidth: `${columns.length * 280}px` }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="flex divide-x divide-gray-100">
          {columns.map(([label, items]) => (
            <div key={label} className="flex-1 p-2 min-w-[260px]">
              <p className="px-3 pt-1 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {label}
              </p>
              {items.map((item) => (
                <MegaMenuItem key={item.to} item={item} />
              ))}
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (splitColumns) {
    const mid = Math.ceil(category.items.length / 2)
    const col1 = category.items.slice(0, mid)
    const col2 = category.items.slice(mid)
    return (
      <div
        ref={panelRef}
        className={cn(
          'absolute top-full mt-1 bg-white rounded shadow-xl border border-gray-200 z-50',
          alignRight ? 'right-0' : 'left-0',
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="flex divide-x divide-gray-100">
          <div className="flex-1 min-w-0 p-2 w-[260px] overflow-hidden">
            {col1.map((item) => (
              <MegaMenuItem key={item.to} item={item} />
            ))}
          </div>
          <div className="flex-1 min-w-0 p-2 w-[260px] overflow-hidden">
            {col2.map((item) => (
              <MegaMenuItem key={item.to} item={item} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={panelRef}
      className={cn(
        'absolute top-full mt-1 bg-white rounded shadow-xl border border-gray-200 min-w-[320px] z-50',
        alignRight ? 'right-0' : 'left-0',
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="p-2">
        {category.items.map((item) => (
          <MegaMenuItem key={item.to} item={item} />
        ))}
      </div>
    </div>
  )
}

export function MegaMenu({ categories = navCategories }: { categories?: NavCategory[] } = {}) {
  const [openCategory, setOpenCategory] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const location = useLocation()
  const isAllowed = useModulesStore((s) => s.isAllowed)

  // Filter categories: hide items where the user has no module access.
  const visibleCategories = categories
    .map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => !item.moduleId || isAllowed(item.moduleId)),
    }))
    .filter((cat) => cat.items.length > 0)

  // Close menu on route change
  useEffect(() => {
    setOpenCategory(null)
  }, [location.pathname])

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenCategory(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenCategory(null)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  function handleMouseEnter(categoryId: string) {
    clearTimeout(timeoutRef.current)
    setOpenCategory(categoryId)
  }

  function handleMouseLeave() {
    timeoutRef.current = setTimeout(() => setOpenCategory(null), 200)
  }

  function isActiveCategory(category: NavCategory) {
    return category.items.some((item) => location.pathname.startsWith(item.to))
  }

  return (
    <nav ref={menuRef} className="hidden xl:flex items-center gap-0.5">
      {visibleCategories.map((category) => {
        // Single-item categories render as direct links instead of dropdowns.
        if (category.items.length === 1) {
          const only = category.items[0]
          return (
            <NavLink
              key={category.id}
              to={only.to}
              end={only.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-1 px-3 py-2 text-sm font-medium rounded transition-colors',
                  isActive
                    ? 'text-white bg-white/10'
                    : 'text-white/80 hover:text-white hover:bg-white/10',
                )
              }
            >
              {category.label}
            </NavLink>
          )
        }
        return (
          <div
            key={category.id}
            className="relative"
            onMouseEnter={() => handleMouseEnter(category.id)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Trigger button */}
            <button
              onClick={() => setOpenCategory(openCategory === category.id ? null : category.id)}
              className={cn(
                'flex items-center gap-1 px-3 py-2 text-sm font-medium rounded transition-colors',
                openCategory === category.id
                  ? 'bg-white/15 text-white'
                  : isActiveCategory(category)
                    ? 'text-white bg-white/10'
                    : 'text-white/80 hover:text-white hover:bg-white/10',
              )}
            >
              {category.label}
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 transition-transform',
                  openCategory === category.id && 'rotate-180',
                )}
              />
            </button>

            {/* Dropdown panel */}
            {openCategory === category.id && (
              <DropdownPanel
                category={category}
                onMouseEnter={() => handleMouseEnter(category.id)}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </div>
        )
      })}
    </nav>
  )
}
